import React, { useState } from "react";

import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf } from "@fortawesome/pro-light-svg-icons";

import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import Grid from "ui/Grid";
import ContactSupportLink from "ui/ContactSupportLink";

import { formatDateShort } from "utils/format";

import { BillingOverviewQuery } from "../Overview/gql/Query.generated";
export type InvoiceType =
  BillingOverviewQuery["getOrganizationBilling"]["invoices"][number];
import { usePayInvoiceMutation } from "./gql/Mutation.generated";
import Button from "ui/Button";
import { useStripe } from "@stripe/react-stripe-js";
import Tip from "ui/Tip";
import { useAsyncActionFlash, useSetFlash } from "providers/WithFlashes";
import ModalContainer from "ui/ModalContainer";
import { faExternalLink } from "@fortawesome/pro-light-svg-icons";

const InvoiceList: React.FunctionComponent<{
  organizationSlug: string;
  organizationCreatedAt: number;
  invoices: InvoiceType[];
  refetchOrganizationBilling: () => void;
}> = ({
  organizationSlug,
  organizationCreatedAt,
  invoices,
  refetchOrganizationBilling,
}) => {
  const stripe = useStripe();
  const setFlash = useSetFlash();
  const [showPaymentInProgress, setShowPaymentInProgress] = useState(false);

  const [payInvoice, { called, loading }] = usePayInvoiceMutation();
  useAsyncActionFlash({
    called: called,
    loading: loading,
  });

  const handlePayInvoice = (invoiceId: string) => {
    setShowPaymentInProgress(true);
    payInvoice({
      variables: { organizationSlug, invoiceId },
      onCompleted: (data) => {
        const successMessage =
          "Payment authentication successful. Your invoice has been paid.";
        // 3D Secure handling, similar to activatePlanOnCompleted in ActivePlan component
        if (!data.payInvoice.organizationBilling.paymentRequiresAction) {
          // 3D Secure not required
          setFlash("success", successMessage);
          setShowPaymentInProgress(false);
          refetchOrganizationBilling();
          return;
        }

        setShowPaymentInProgress(false);
        stripe
          .confirmCardPayment(
            data.payInvoice.organizationBilling.stripeClientSecret,
          )
          .then(({ paymentIntent, error }) => {
            if (error || paymentIntent.status !== "succeeded") {
              setFlash(
                "error",
                "Payment authentication failed. Please update to a different payment method or contact support.",
              );
            } else {
              setFlash("success", successMessage);
            }
            refetchOrganizationBilling();
          });
      },
      onError: (data) => {
        if (data.message === "Invoice is already paid") {
          // The list can be stale: the invoice may have been paid on the hosted page, in
          // another tab, or by Stripe's automatic retry.
          setFlash(
            "success",
            "This invoice is already paid. Refreshing the invoice list.",
          );
        } else {
          setFlash("error", data.message);
        }
        refetchOrganizationBilling();
        setShowPaymentInProgress(false);
      },
    });
  };
  return (
    <Panel id="invoices" title="Invoices">
      <Grid
        data={invoices}
        noRowsText={<em>(none yet)</em>}
        defaultSortBy="created"
        columns={[
          {
            field: "created",
            header: "Date",
            width: "110px",
            defaultSortOrder: "DESC",
            renderer: function DateCell({ fieldData, rowData }) {
              const date = formatDateShort(moment.unix(fieldData));
              return rowData.invoicePdf ? (
                <a href={rowData.invoicePdf} title="Download invoice PDF">
                  {date}
                </a>
              ) : (
                date
              );
            },
          },
          {
            field: "reference",
            header: "Reference",
            width: "150px",
          },
          {
            field: "formattedTotal",
            header: "Amount",
            width: "120px",
            disableSort: true,
          },
          {
            field: "status",
            header: "Status",
            width: "minmax(400px,1fr)",
            renderer: function StatusCell({ rowData, fieldData }) {
              return (
                <span className="flex">
                  <span className="w-[80px]">{fieldData}</span>
                  {fieldData == "Open" && (
                    <>
                      <div className="flex items-start">
                        <Button
                          bare
                          onClick={() => handlePayInvoice(rowData.id)}
                          variant="link"
                        >
                          Pay now
                        </Button>
                        <Tip
                          className="pl-1"
                          content={
                            <>
                              By clicking "Pay now", you authorize us to charge
                              this invoice using your current payment method.
                            </>
                          }
                        />
                      </div>
                      {rowData.hostedInvoiceUrl && (
                        <div className="pl-1 flex items-start">
                          or use
                          <a
                            className="pl-1"
                            href={rowData.hostedInvoiceUrl}
                            target="_blank"
                          >
                            fallback payment page{" "}
                            <FontAwesomeIcon icon={faExternalLink} />
                          </a>
                          <Tip
                            className="pl-1"
                            content={
                              <>
                                If you're not able to complete the payment with
                                the "Pay now" button, try paying this invoice
                                using the external Payment Page.
                              </>
                            }
                          />
                        </div>
                      )}
                    </>
                  )}
                </span>
              );
            },
          },
          {
            field: "invoicePdf",
            header: "Invoice PDF",
            width: "120px",
            disableSort: true,
            renderer: function InvoicePdfCell({ fieldData }) {
              if (!fieldData) {
                return null;
              }
              return (
                <a href={fieldData} title="Download invoice PDF">
                  <FontAwesomeIcon
                    className="pb-[1px] h-[15px]"
                    icon={faFilePdf}
                  />
                </a>
              );
            },
          },
        ]}
      />
      {moment
        .unix(organizationCreatedAt)
        .isBefore(moment(new Date(2022, 1, 1))) && (
        <PanelSection>
          For retrieving invoices before January 1st, 2022,{" "}
          <ContactSupportLink simpleStyle>
            please contact support
          </ContactSupportLink>
          .
        </PanelSection>
      )}
      {showPaymentInProgress && (
        <ModalContainer
          title="Processing payment..."
          layout="centered"
          onClose={() => setShowPaymentInProgress(false)}
        >
          Your payment is being processed. Please wait for confirmation.
        </ModalContainer>
      )}
    </Panel>
  );
};

export default InvoiceList;
